import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
} from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { getCustomerById } from "../../../api/customerAPI";
import { generateCustomerAgreement } from "../../../api/agreement";
import CustomerAgreementTemplate from "./template";
import getPopulatedValues from "../../../components/customerManagement/populateDefaultValues";
import CustomerAgreementEditModal from "./edit-agreement";
import Toast from "../../../common/Toast";
import Breadcrumbs from "../../../common/Breadcrumbs";
import moment from "moment";
import colors from "../../../theme/Colours";

export interface AgreementFormData {
  clientName: string;
  clientAddress: string;
  clientSignatory: string;
  outletAddress: string;
  sameAsClientAddress: boolean;
  assets: { name: string; quantity: number }[];
  sellerName?: string;
  sellerAddress?: string;
  sellerSignatory?: string;
  effectiveDate?: string;
  depositAmount?: string;
  executionDate?: string;
  paymentDetailsByClient?: string;
  sellerAccountDetails?: string;
  amountInFigures?: string;
  amountInWords?: string;
  depositAmountInFigures?: string;
  products?: { name: string; price: number }[];
}

const initialFormData: AgreementFormData = {
  clientName: "",
  clientAddress: "",
  clientSignatory: "",
  outletAddress: "",
  sameAsClientAddress: false,
  assets: [],
  sellerName: "",
  sellerAddress: "",
  sellerSignatory: "",
  effectiveDate: "",
  depositAmount: "",
  executionDate: "",
  paymentDetailsByClient: "",
  sellerAccountDetails: "",
  amountInFigures: "",
  amountInWords: "",
  depositAmountInFigures: "",
  products: [],
};

export default function AgreementsPage() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const customerId = params.id || "";
  const [toggle, setToggle] = useState(false);
  const [customerData, setCustomerData] = useState<any>(null);
  const [values, setValues] = useState(initialFormData);
  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (customerId) {
      const fetchCustomerData = async () => {
        try {
          const response = await getCustomerById(customerId);

          if (!response) {
            console.error("Failed to fetch customer data");
            return;
          }

          const populatedValues = getPopulatedValues(response.data.data);
          console.log(
            "Customer Agreements Data:",
            populatedValues.customerAgreements
          );
          console.log(
            "Unsigned Agreement:",
            populatedValues.customerAgreements?.unsigned
          );
          setCustomerData(populatedValues);
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      };

      fetchCustomerData();
    }
  }, [customerId]);

  useEffect(() => {
    const loadAgreement = async () => {
      if (customerData?.customerAgreements?.unsigned) {
        const agreementContext = customerData?.agreementContext;
        const sellerDetails = customerData?.sellerDetails;

        const getDateString = (dateString: string) => {
          if (!dateString.length) return "";

          const date = new Date(dateString);
          const formatted = date.toLocaleDateString("en-GB");

          return formatted;
        };

        setValues({
          ...values,
          clientName: customerData?.step1?.outletName || "",
          clientAddress: customerData?.step1?.registeredAddress || "",
          clientSignatory:
            agreementContext.clientRepresentativeName ||
            customerData?.step1?.authorizedSignatory ||
            "",
          outletAddress:
            customerData?.step1?.outletAddress ||
            customerData?.step1?.registeredAddress ||
            "",
          assets: agreementContext.assets || [],
          effectiveDate: getDateString(agreementContext.effectiveDate || ""),
          executionDate: getDateString(agreementContext.executionDate || ""),
          depositAmount: agreementContext.depositAmount || "",
          sellerName: sellerDetails?.name || agreementContext.sellerName || "",
          sellerAddress:
            sellerDetails?.registeredAddress ||
            sellerDetails?.companyName ||
            agreementContext.sellerAddress ||
            "",
          sellerSignatory:
            sellerDetails?.legalSignatory ||
            sellerDetails?.name ||
            agreementContext.sellerSignatory ||
            "",
          paymentDetailsByClient: agreementContext.paymentDetailsByClient || "",
          sellerAccountDetails: agreementContext.sellerAccountDetails || "",
          amountInFigures: agreementContext.amountInFigures || "",
          amountInWords: agreementContext.amountInWords || "",
          depositAmountInFigures: agreementContext.depositAmountInFigures || "",
          products: agreementContext.products || [],
        });
      } else {
        const sellerDetails = customerData?.sellerDetails;
        setValues({
          ...values,
          clientName: customerData?.step1?.outletName || "",
          clientAddress: customerData?.step1?.registeredAddress || "",
          clientSignatory: customerData?.step1?.authorizedSignatory || "",
          outletAddress:
            customerData?.step1?.outletAddress ||
            customerData?.step1?.registeredAddress ||
            "",
          sellerName: sellerDetails?.name || "",
          sellerAddress:
            sellerDetails?.registeredAddress ||
            sellerDetails?.companyName ||
            "",
          sellerSignatory:
            sellerDetails?.legalSignatory || sellerDetails?.name || "",
        });
      }
    };

    loadAgreement();
  }, [customerData]);

  const openEditToggle = () => {
    setToggle(true);
  };

  const closeEditToggle = () => {
    setToggle(false);
  };

  const handleGenerateCustomerAgreement = async () => {
    if (customerId) {
      const convertToExactDate = (date: any) => {
        const now = moment();
        const parsedDate = moment(date, "DD/MM/YYYY");
        parsedDate.set({
          hour: now.hour(),
          minute: now.minute(),
          second: now.second(),
        });

        return parsedDate.toDate();
      };

      try {
        const payload = {
          clientName: values.clientName,
          clientAddress: values.clientAddress,
          clientSignatory: values.clientSignatory,
          outletAddress: values.outletAddress,
          assets: values.assets,
          sellerName: values.sellerName,
          sellerAddress: values.sellerAddress,
          sellerSignatory: values.sellerSignatory,
          effectiveDate: values.effectiveDate,
          depositAmount: values.depositAmount,
          executionDate: values.executionDate,
          paymentDetailsByClient: values.paymentDetailsByClient,
          sellerAccountDetails: values.sellerAccountDetails,
          amountInFigures: values.amountInFigures,
          amountInWords: values.amountInWords,
          depositAmountInFigures: values.depositAmountInFigures,
          products: values.products,
        };

        const response = await generateCustomerAgreement({
          customerId,
          payload,
        });

        if (response?.data?.data?.[0]) {
          const agreementData = response.data.data[0];

          // Download the generated agreement file
          if (agreementData.url && agreementData.fileName) {
            const link = document.createElement("a");
            link.href = agreementData.url;
            link.download = agreementData.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }

          setError(false);
          setShowToast(true);
          setToastMessage("Generated customer agreement successfully");

          // Redirect back to customer details with refresh state
          setTimeout(() => {
            navigate(`/maltora-seller/customer/${customerId}`, {
              state: { refreshData: true },
            });
          }, 2000);
        }
      } catch (error) {
        console.error("Failed to generate customer agreement:", error);
        setError(true);
        setShowToast(true);
        setToastMessage("Failed to generate customer agreement");
      }
    }
  };

  return (
    <>
      <Breadcrumbs
        paths={["All Customers", "Customer Details", "Agreement"]}
        links={[
          "/maltora-seller/all-customers",
          `/maltora-seller/customer/${customerId}`,
        ]}
      />
      <Card sx={{ padding: "20px", boxShadow: 2 }}>
        <CardContent sx={{ padding: "0px !important" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4">Customer Agreement</Typography>
            <Button
              onClick={openEditToggle}
              variant="outlined"
              sx={{
                border: "1px solid #FF6B00",
                color: "#FF6B00",
                backgroundColor: "#FFF8F3",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                minWidth: "unset",
                width: "92px",
                height: "36px",
                borderRadius: "10px",
                padding: "6px 14px",
                boxShadow: "0px 2px 8px rgba(255, 107, 0, 0.08)",

                "& .MuiButton-startIcon": {
                  marginRight: "6px",
                },

                "&:hover": {
                  border: "1px solid #FF6B00",
                  backgroundColor: "#FFF1E7",
                  boxShadow: "0px 4px 12px rgba(255, 107, 0, 0.12)",
                },
              }}
            >
              Edit
            </Button>
          </Box>
          <Box sx={{ maxHeight: 400, overflow: "auto", my: 3 }}>
            <CustomerAgreementTemplate
              placeholders={{
                SELLER_NAME: values.sellerName || "",
                SELLER_ADDRESS: values.sellerAddress || "",
                SELLER_SIGNATORY: values.sellerSignatory || "",
                CLIENT_NAME: values.clientName || "",
                CLIENT_ADDRESS: values.clientAddress || "",
                CLIENT_SIGNATORY: values.clientSignatory || "",
                OUTLET_ADDRESS:
                  values.outletAddress || values.clientAddress || "",
                EFFECTIVE_DATE: values.effectiveDate || "",
                DEPOSIT_AMOUNT: values.depositAmount || "",
                EXECUTION_DATE: values.executionDate || "",
                PAYMENT_DETAILS_BY_CLIENT: values.paymentDetailsByClient || "",
                SELLER_ACCOUNT_DETAILS: values.sellerAccountDetails || "",
                AMOUNT_IN_FIGURES: values.amountInFigures || "",
                AMOUNT_IN_WORDS: values.amountInWords || "",
                DEPOSIT_AMOUNT_IN_FIGURES: values.depositAmountInFigures || "",
                ASSETS: values.assets || [],
                PRODUCTS: values.products || [],
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              p: 2,
              pt: 3,
            }}
          >
            <Button
              size="small"
              variant="contained"
              sx={{ backgroundColor: colors.primaryButtonOrange, width: 120 }}
              onClick={handleGenerateCustomerAgreement}
            >
              {(() => {
                const hasUnsigned = customerData?.customerAgreements?.unsigned;
                const hasDocumentId =
                  customerData?.customerAgreements?.unsigned?.documentId;
                console.log("Button condition check:", {
                  hasUnsigned,
                  hasDocumentId,
                });
                return hasUnsigned && hasDocumentId ? "Generate" : "Generate";
              })()}
            </Button>
          </Box>
        </CardContent>
      </Card>
      <CustomerAgreementEditModal
        open={toggle}
        values={values}
        setValues={setValues}
        handleClose={closeEditToggle}
      />
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
    </>
  );
}
